import { useState, type ReactElement, type SubmitEvent } from "react";
import { useSearchParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { NewTabIcon } from "../../components/new-tab-icon.js";
import { FormSummary } from "../../forms/fields.js";
import { signupRules } from "../../forms/view-model-rules.js";

import { ConfirmationView } from "./confirmation-view.js";
import { CheckField, ValueField } from "./contract-fields.js";
import type { Confirmation, ContractKind, PlanForm } from "./contract-types.js";
import { ContractorFields } from "./contractor-fields.js";
import { LoadingPage } from "./loading-page.js";
import { invoiceSelectable, isOn, needsPayment } from "./plan-form.js";
import { PlanOptions } from "./plan-options.js";
import { valuesFromQuery } from "./plan-query.js";
import { PlanSelection, PlanTotal } from "./plan-selection.js";
import { tenantApi, tenantUrls } from "./urls.js";
import { useContractFlow, type ContractFlow } from "./use-contract-flow.js";

/** 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。 */
export const signupDialog = (value: Confirmation): string =>
    `「${value.title ?? ""}」を${value.planDisplayName}で申し込みます。月額（税込）は ${value.priceLabel} です。よろしいですか？`;

/** 新規申し込み（<c>/Tenant/New</c> → 確認 → 確定）。 */
export const signupKind: ContractKind = {
    inputTitle: "新規申し込み",
    confirmTitle: "お申し込み内容の確認",
    confirmLead: "内容をご確認のうえ、お申し込みを確定してください。",
    trialNote: "この期間は料金がかかりません。",
    cardButton: "カード情報を入力して申し込む",
    submitButton: "申し込みを確定する",
    cardSubmitText: "申し込む",
    confirmUrl: tenantApi.signupConfirm,
    commitUrl: tenantApi.signup,
    rules: signupRules,
    contractorInput: true,
    dialog: signupDialog,
};

/** 新規申し込みにしか無い欄。 */
const signupFields = [
    "Company", "UserName", "Title", "ContractorDepartment", "ContractorPostalCode", "ContractorAddress", "ContractorTel",
    "ContractorEmail", "Agree",
] as const;

/**
 * ご契約者情報（企業名・お名前・システム名・連絡先・請求書払い）。
 * 連絡先は<b>有償プランでは部署名のほかが必須</b>、無償プランでは任意。
 * <b>請求先は持たない</b>——請求書の宛先は宛名（空なら企業名）だけ。
 */
function ContractorSection({ flow }: { readonly flow: ContractFlow }): ReactElement {
    const { values, change, fieldError } = flow;

    return (
        <section className="card mb-4 p-6" data-contractor="">
            <h2 className="section-title">ご契約者情報</h2>
            <div className="grid gap-4">
                <ValueField
                    label="企業名/組織名" name="Company" required autoComplete="organization"
                    value={values["Company"] ?? ""} onChange={change} error={fieldError("Company")}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                    <ValueField
                        label="お名前" name="UserName" required autoComplete="name"
                        value={values["UserName"] ?? ""} onChange={change} error={fieldError("UserName")}
                    />
                    <ValueField
                        label="システム名" name="Title" required help="環境の表示名です。" placeholder="社内業務システム"
                        value={values["Title"] ?? ""} onChange={change} error={fieldError("Title")}
                    />
                </div>
                <ContractorFields
                    values={values} required={needsPayment(flow.plan)} fieldError={fieldError} onChange={change}
                />
                {/* 支払方法は料金の発生するプランでだけ。請求書払いは従量課金のプランだけ選べる */}
                {needsPayment(flow.plan)
                    ? (
                        <div data-plan-payment="">
                            {invoiceSelectable(flow.plan)
                                ? (
                                    <CheckField
                                        label="請求書払いを希望する" name="PaymentOnInvoice"
                                        checked={isOn(values, "PaymentOnInvoice")} onChange={change}
                                    />
                                )
                                : null}
                        </div>
                    )
                    : null}
            </div>
        </section>
    );
}

function SignupInput({ form, flow }: { readonly form: PlanForm; readonly flow: ContractFlow }): ReactElement {
    usePageMeta(signupKind.inputTitle);
    const agreeError = flow.fieldError("Agree");
    const section = {
        plans: form.plans,
        plan: flow.plan,
        values: flow.values,
        showInitialStorage: form.showInitialStorage,
        fieldError: flow.fieldError,
        onChange: flow.change,
        onSelectPlan: flow.selectPlan,
    };

    const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        flow.confirm(event.currentTarget);
    };

    return (
        <div className="page max-w-3xl">
            <h1 className="page-title">{signupKind.inputTitle}</h1>
            <p className="page-lead">ご利用内容を入力してください。</p>

            <form
                method="post" noValidate onSubmit={onSubmit}
                onBlur={(event) => { flow.leave(event.target.getAttribute("name") ?? ""); }}
            >
                <FormSummary messages={flow.summary} />

                <PlanTotal plan={flow.plan} values={flow.values} />
                <PlanSelection {...section} />
                <PlanOptions {...section} />
                <ContractorSection flow={flow} />

                <section className="card mb-7 p-6">
                    {/* 同意の前に本文を読めるようにする。申し込みを確定すると、この版に同意したことが記録される */}
                    <p className="mb-3 text-sm text-slate-700">
                        お申し込みには、
                        <a href="/Home/Terms" target="_blank" rel="noopener" className="inline-link">利用規約<NewTabIcon /></a>
                        への同意が必要です。
                        ご入力いただいた情報は、本サービスのご提供・ご請求・ご連絡に利用します。取り扱いは
                        <a href="https://implem.co.jp/privacy" target="_blank" rel="noopener noreferrer" className="inline-link">プライバシーポリシー<NewTabIcon /></a>
                        をご覧ください。
                    </p>
                    <CheckField
                        label="利用規約に同意する" name="Agree"
                        checked={isOn(flow.values, "Agree")} onChange={flow.change}
                    />
                    <span
                        className={`form-error ${agreeError === undefined ? "field-validation-valid" : "field-validation-error"}`}
                        data-valmsg-for="Agree"
                    >
                        {agreeError}
                    </span>
                </section>

                <div className="flex gap-3">
                    <a href={tenantUrls.list} className="btn-secondary">キャンセル</a>
                    <button type="submit" className="btn-accent grow" disabled={flow.busy}>確認画面へ</button>
                </div>
            </form>
        </div>
    );
}

/**
 * 入力と確認。<b>料金プランのページから来たら、選んだ内容（クエリ）を初期値にする</b>（<c>plan-query.ts</c>）。
 * 読めない・範囲外の値は無視して API の初期値のまま。開いたときに 1 度だけ当てる（入力を変えた後に URL へは戻さない）。
 */
function SignupFlow({ form }: { readonly form: PlanForm }): ReactElement {
    const [search] = useSearchParams();
    const [initial] = useState(() => ({ ...form, values: valuesFromQuery(form.plans, form.values, search) }));
    const flow = useContractFlow(initial, signupKind, signupFields);

    return flow.step === "confirm"
        ? <ConfirmationView flow={flow} kind={signupKind} />
        : <SignupInput form={form} flow={flow} />;
}

/** 新規申し込み。<b>確認は同じ URL のまま</b>、履歴には 1 段積む（ブラウザの「戻る」で入力へ戻る）。 */
export function SignupPage(): ReactElement {
    const data = useApiData<PlanForm>(tenantApi.signup);

    return data.state === "ready"
        ? <SignupFlow form={data.value} />
        : <LoadingPage title={signupKind.inputTitle} data={data} />;
}
